'use client';

import type { ReactElement } from 'react';
import type { RefinementProps } from '@/components/eataly-plp-filters/lib/types';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export type RefineCheckboxListProps = RefinementProps & {
    emptyMessage?: string;
    className?: string;
};

export default function RefineCheckboxList({
    values,
    attributeId,
    isFilterSelected,
    toggleFilter,
    isPending,
    emptyMessage,
    className,
}: RefineCheckboxListProps): ReactElement {
    if (values.length === 0 && emptyMessage) {
        return <p className="body-copy-m text-neutral-40">{emptyMessage}</p>;
    }

    return (
        <ul
            className={cn(
                'flex max-h-[280px] flex-col gap-4 overflow-y-auto pr-2',
                className,
                '[scrollbar-width:thin] [scrollbar-color:var(--brown-10)_var(--sand-90)]',
                '[&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded-2xl [&::-webkit-scrollbar-track]:bg-sand-90',
                '[&::-webkit-scrollbar-thumb]:rounded-2xl [&::-webkit-scrollbar-thumb]:bg-brown-10'
            )}>
            {values.map((value, idx) => {
                const id = `refine-${attributeId}-${idx}`;
                const isSelected = isFilterSelected(attributeId, value.value);
                const valueLabel = value.label || value.value;

                return (
                    <li key={`${attributeId}:${value.value}`}>
                        <Label
                            htmlFor={id}
                            variant="default"
                            className={cn(
                                'ps-0.5 flex items-center gap-2',
                                isPending ? 'cursor-default' : 'cursor-pointer'
                            )}>
                            <Checkbox
                                id={id}
                                checked={isSelected}
                                disabled={isPending}
                                onCheckedChange={() => toggleFilter(attributeId, value.value)}
                            />
                            <span className="body-copy-m min-w-0 flex-1 text-neutral-10">{valueLabel}</span>
                        </Label>
                    </li>
                );
            })}
        </ul>
    );
}
